import {makeSyncStoreUtil} from '@features/utils/syncStoreUtil';
import {type GenerationStatus} from './streamAgent';
import type {GenerationManager} from './GenerationManager';
import {streamAgent} from './streamAgent';
import type {ClarifyingQuestion} from '../prompt/agent/tools/common/messages';
export class GenerationScreenState {
  currentStruct: string | null = null;
  newName: string | null = null;
  status: GenerationStatus = 'idle';
  generationPromise: Promise<void> | null = null;
  generationReason: 'auto' | 'sendmessage' | null = null;
  hasQueuedGeneration = false;
  clarifyingQuestions: ClarifyingQuestion[] | null = null;

  generationUpdates: {details?: string; update: string}[] = [];
  generationUpdatesStore = makeSyncStoreUtil();

  #abortController: AbortController = new AbortController();
  #abortToken = globalThis.crypto.randomUUID();
  #generationOrigin: GenerationScreenState | null = null;
  #stoppedByUser = false;

  flowId;
  screenId;
  store;
  parent;
  constructor(flowId: string, screenId: string, parent: GenerationManager) {
    this.flowId = flowId;
    this.screenId = screenId;
    this.parent = parent;
    this.store = makeSyncStoreUtil(parent.allScreenStatuses);
  }

  stopGeneration() {
    const origin = this.#generationOrigin ?? this;
    this.hasQueuedGeneration = false;
    this.#stoppedByUser = true;
    origin.hasQueuedGeneration = false;
    origin.#stoppedByUser = true;
    origin.#abortController.abort();
    this.parent.sendAbortGeneration(origin.#abortToken);
    origin.#abortToken = globalThis.crypto.randomUUID();
  }

  triggerDownload(reason: Exclude<typeof this.generationReason, null>) {
    if (this.generationPromise) {
      // Generation is already in progress.
      // For 'sendmessage': queue re-validation to pick up the new message after current gen completes.
      // For 'auto': skip silently - current generation will fetch latest data anyway.
      // If user explicitly stopped, don't queue — the abort will settle the promise shortly.
      if (reason === 'auto' || this.#stoppedByUser) {
        return;
      }

      this.hasQueuedGeneration = true;
      this.generationReason = 'sendmessage';
      this.store.trigger();

      return;
    }

    // User explicitly stopped generation. Ignore stale triggers
    // (e.g. from in-flight pushChatMessage onSuccess that resolves after stop).
    if (this.#stoppedByUser) {
      this.#stoppedByUser = false;
      return;
    }

    this.generationUpdates = [];
    this.generationUpdatesStore.trigger();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    this.generationReason = reason;
    this.#generationOrigin = this;

    const initialScreenIds = this.parent.getInitialScreenIds(this.flowId);

    this.generationPromise = streamAgent({
      flowId: this.flowId,
      screenId: this.screenId,
      abortToken: this.#abortToken,

      setState: (status) => {
        this.status = status;
        this.store.trigger();
      },

      updateScreen: (screenId, html, newName) => {
        const screen = this.parent.getScreen(this.flowId, screenId, false);
        screen.currentStruct = html;
        screen.newName = newName;
        if (screen.generationPromise !== this.generationPromise) {
          screen.generationPromise = this.generationPromise;
          screen.status = this.status;
          screen.#generationOrigin = this;
          screen.#cleanupWhenDone();
        }
        screen.store.trigger();
      },
      pushUpdate: (update: string, details?: string) => {
        this.generationUpdates = [...this.generationUpdates, {update, details}];
        this.generationUpdatesStore.trigger();
      },
      getScreenContents: this.parent.fetchScreenContents,
      invalidateScreen: this.parent.invalidate,
      setGentype: this.parent.setScreenType,

      signal: this.#abortController.signal,

      onClarifyingQuestions: (questions) => {
        this.clarifyingQuestions = questions;
        this.store.trigger();
      },
      onClarifyingQuestionsDone: () => {
        this.clarifyingQuestions = null;
        this.store.trigger();
      },
      onSessionLimit: (info) => {
        this.parent.onSessionLimit({
          ...info,
          flowId: this.flowId,
          screenId: this.screenId,
        });
      },
    }).then((result) => {
      if (!result) return;
      if (result.stubScreenIds.has(this.screenId)) {
        this.hasQueuedGeneration = true;
      }
      this.parent.logScreenEvents(
        this.flowId,
        initialScreenIds,
        result.touchedScreenIds,
      );
    });

    this.#cleanupWhenDone();
  }

  #cleanupWhenDone() {
    void this.generationPromise
      ?.catch(() => {
        // Abort or network errors — reset state before the finally block.
        this.currentStruct = null;
        this.newName = null;
        this.status = 'idle';
        this.store.trigger();
      })
      .finally(() => {
        this.generationPromise = null;
        this.status = 'idle';
        this.currentStruct = null;
        this.newName = null;
        this.clarifyingQuestions = null;
        this.#stoppedByUser = false;

        this.store.trigger();

        if (this.hasQueuedGeneration) {
          this.hasQueuedGeneration = false;
          this.triggerDownload('sendmessage');
        } else {
          this.parent.startNextResume(this.flowId);
        }
      });
  }

  get isGenerating() {
    if (this.status === 'idle') {
      return false;
    }

    // If we're sending a message, we always want to show the spinner
    // If we're just automatically revalidating after connect,
    // then we only show the spinner *after* we know there's content
    if (this.status === 'connecting') {
      return this.generationReason === 'sendmessage';
    }

    // In all other cases, we're generating
    return true;
  }
}
